import { App as CapApp, type URLOpenListenerEvent } from "@capacitor/app";
import type { RegisteredRouter } from "@tanstack/react-router";
import { Browser } from "@capacitor/browser";
import { getDefaultStore } from "jotai";
import { logDeepLink } from "@/lib/breadcrumbs";
import { deepLinkAtom } from "@/lib/atoms/deep-link-atom";
import { DEEP_LINK_ALLOWED_URLS } from "@/lib/utils/config";

const store = getDefaultStore();

// Rotas de callback de uso único: navegar até elas (abaixo) já consome o token
// e cria/atualiza a sessão — o oauth-callback do Stytch cria a sessão do login
// social e o create-password do reset de senha redefine a senha e cria a sessão.
// Se ficassem no atom, o redirectToDeepLink as reexecutaria assim que o usuário
// chegasse numa rota fora de `/auth` (ex.: home pós-login), com o token já gasto,
// reautenticando em vão e jogando o usuário de volta para a tela anterior do
// fluxo. Por isso não as persistimos. Demais deep-links (inclusive Auth0) inalterados.
// O /google/callback segue a mesma regra: o code do Google Agenda só vale uma troca.
const SINGLE_USE_AUTH_PATHS = [
  "/auth/oauth-callback",
  "/auth/password-reset/create-password",
  "/google/callback",
];

const isSingleUseAuthPath = (slug: string): boolean =>
  SINGLE_USE_AUTH_PATHS.some((path) => slug.startsWith(path));

// O Google Agenda volta para a tela que abriu o Browser, então o callback toma o lugar dela no
// histórico: o voltar a partir da tela de destino não reabre a mesma tela.
const GOOGLE_CALENDAR_CALLBACK_PATH = "/google/callback";

export const initDeepLink = async (router: RegisteredRouter): Promise<void> => {
  await CapApp.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
    logDeepLink({ location: router.latestLocation, deeplinkUrl: event.url });
    void parseDeepLink(event.url, router, true);
  });
};

export const parseDeepLink = async (
  eventUrl: string,
  router: RegisteredRouter,
  isExternal: boolean,
): Promise<boolean> => {
  const foundKey: string | undefined = DEEP_LINK_ALLOWED_URLS.find((key: string) =>
    eventUrl.includes(key),
  );
  if (foundKey) {
    // Esse é um deep-link somewhat válido
    let slug = eventUrl.replace(foundKey, "");
    // Se não sobrou nada, filtra
    slug = slug === "" ? "/" : slug;
    if (isExternal) {
      // Não persiste rotas de callback de uso único (ver SINGLE_USE_AUTH_PATHS).
      if (!isSingleUseAuthPath(slug)) {
        store.set(deepLinkAtom, slug);
      }
      // Se existe um navegador aberto, fecha
      try {
        await Browser.close();
      } catch (e) {
        // eslint-disable-next-line no-console -- Console.error
        console.error(e);
      }
    }
    // Navega para a próxima página
    const replace = isExternal && slug.startsWith(GOOGLE_CALENDAR_CALLBACK_PATH);
    await router.navigate({ to: slug, ...(replace && { replace }) });
    return true;
  }

  return false;
};
